import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as CodeIcon } from '@/assets/icons/code-icon.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil-icon.svg';
import { ReactComponent as RingsIcon } from '@/assets/icons/rings-icon.svg';
import { ActionsDropdownButton } from '@/components/ActionsDropdown';
import Text from '@/components/Text';
import useToasts from '@/hooks/useToasts';
import { StockLibraryImageResource } from '@/types/resources';
import { api } from '@/utils/api';
import { hexToRgba } from '@/utils/theme';

import ImageToolCheckbox from './ImageToolCheckbox';
import ImageToolLoadingImage from './ImageToolLoadingImage';
import PromptBox from './PromptBox';
import PromptModal from './PromptModal';

const Box = styled.div`
  margin-bottom: 16px;
  page-break-inside: avoid;
`;

const InstaBox = styled.div`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  padding: 16px;
`;

const InstaText = styled(Text)`
  font-weight: 500;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  &:last-child {
    margin-bottom: 0;
  }
  svg {
    margin-right: 12px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const InstaLink = styled(InstaText)`
  a {
    text-decoration: underline;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Img = styled.img`
  width: 100%;
  display: block;
`;

const StyledActionsDropdownButton = styled(ActionsDropdownButton)`
  height: 48px;
`;

const Source = styled(Text)`
  position: absolute;
  top: 16px;
  left: 16px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 100px;
  height: 27px;
  display: flex;
  align-items: center;
  padding: 0 12px;
`;

const DownloadCount = styled(Text)`
  position: absolute;
  top: 16px;
  right: 16px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 100px;
  height: 27px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  svg {
    margin-right: 6px;
  }
`;

const DownloadButton = styled.button<{ $standalone?: boolean }>`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  height: 32px;
  width: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: ${({ $standalone = false }) => ($standalone ? 16 : 60)}px;
  bottom: 16px;
  transition: all ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
  opacity: 0;
`;

const LightBoxButton = styled(DownloadButton)`
  left: 16px;
`;

const FavoritesButton = styled(DownloadButton)<{ $isFavorite?: boolean }>`
  top: 16px;
  bottom: auto;
  left: 16px;
  height: 27px;
  width: 40px;
  border-radius: 100px;
  ${({ $isFavorite }) =>
    $isFavorite &&
    css`
      opacity: 1;
    `}
`;

const MoreButtonHolder = styled.div`
  position: absolute;
  right: 16px;
  bottom: 16px;
  opacity: 0;
  transition: all ease-out 0.2s;
`;

const MoreButton = styled(DownloadButton)`
  position: static;
`;

const Dropdown = styled.div`
  position: absolute;
  bottom: 40px;
  width: 290px;
  right: 0;
  padding: 4px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
`;

const Overlay = styled.div`
  background: rgba(0, 0, 0, 0.12);
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  transition: opacity ease-out 0.2s;
`;

const StyledImageToolCheckbox = styled(ImageToolCheckbox)`
  opacity: 0;
  transition: opacity ease-out 0.2s;
`;

const ImageHolder = styled.div`
  position: relative;
  &:hover {
    ${Overlay}, ${DownloadButton}, ${StyledImageToolCheckbox}, ${MoreButtonHolder} {
      opacity: 1;
    }
  }
`;

const PlayButton = styled.a`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  color: ${({ theme }) => theme.colors.white};
  display: inline-block;
  width: 72px;
  height: 72px;
  border: 1px solid transparent;
  border-radius: 50%;
  transition: 0.2s ease-out border-color;
  background-color: ${({ theme }) => hexToRgba(theme.colors.white, 0.5)};
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    border-color: ${({ theme }) => theme.colors.white};
  }
`;

interface Props {
  image: StockLibraryImageResource;
  type: 'library' | 'midjourney' | 'instagram' | 'getty';
  selectedImages: number[];
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
  onClick: () => void;
  aspectRatio?: string;
}

const ImageToolImage: FC<Props> = ({
  image,
  type,
  selectedImages,
  setSelectedImages,
  onClick,
  aspectRatio,
}) => {
  const [showMJDropdown, setShowMjDropdown] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);

  const [extraMJdisabled, setExtraMJdisabled] = useState(false);

  const { id } = useParams();

  const toasts = useToasts();

  const queryClient = useQueryClient();

  const [downloadsCount, setDownloadsCount] = useState(
    image.meta!.downloads ?? 0,
  );

  if (image.meta?.status === 'pending') {
    return (
      <ImageToolLoadingImage
        aspectRatio={aspectRatio}
        query={image.meta?.query}
      />
    );
  }

  const extraMJGenerate = async (
    e: MouseEvent<HTMLButtonElement>,
    type: 'query' | 'visual' | 'prompt',
  ) => {
    e.stopPropagation();
    setShowMjDropdown(false);

    if (type === 'prompt') {
      return setShowPromptModal(true);
    }

    const toastId = toasts.loading();
    setExtraMJdisabled(true);

    try {
      await api.post<never, { id: number }>(
        `/v1/image-generation/${image.id}/additional-variations`,
        {
          type,
        },
      );

      await queryClient.invalidateQueries(
        ['image_generation_project', { id }],
        {
          exact: true,
        },
      );

      toasts.success(toastId);
    } catch (_) {
      toasts.error(toastId);
    }

    setExtraMJdisabled(false);
  };

  const onDownload = (e: MouseEvent<HTMLAnchorElement>) => {
    if (image.meta!.licensed) {
      e.preventDefault();
      window.open(image.meta!.pageUrl, '_blank');
      return;
    }
    setDownloadsCount((prev) => prev + 1);
  };

  const onToggleFavorite = async () => {
    await api.patch<never, { id: number }>(
      `/v1/image-generation/${image.id}/favourite`,
    );

    await queryClient.invalidateQueries(['image_generation_project', { id }], {
      exact: true,
    });
  };

  return (
    <Box>
      <ImageHolder>
        <Img
          src={image.versions.preview ?? image.versions.original}
          loading="lazy"
          alt={image.name}
        />
        {type === 'library' && image.meta!.provider && (
          <Source $type="smallHeadline">{image.meta!.provider}</Source>
        )}
        {type === 'getty' && image.meta!.provider && (
          <Source $type="smallHeadline">Getty Images</Source>
        )}
        {type === 'instagram' && (
          <Source $type="smallHeadline">Instagram</Source>
        )}
        <Overlay />
        {!image.meta?.isVideo ? (
          <>
            {type !== 'instagram' && !image.meta!.licensed && (
              <DownloadCount $type="smallHeadline">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.23047 7.80724L9.69201 1.3457"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.69276 6.88416V1.3457H4.1543"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1.38477 10.5762H10.6155"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {downloadsCount}
              </DownloadCount>
            )}
            {type === 'instagram' && (
              <DownloadCount $type="smallHeadline">
                <svg
                  width="14"
                  height="15"
                  viewBox="0 0 14 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.99972 12.2147C6.99972 12.2147 1.64258 9.21471 1.64258 5.57185C1.64269 4.92799 1.86579 4.30405 2.27394 3.80609C2.6821 3.30814 3.25012 2.96691 3.88143 2.84043C4.51274 2.71394 5.16837 2.81001 5.73686 3.1123C6.30534 3.41458 6.75159 3.90443 6.99973 4.49855L6.99972 4.49855C7.24785 3.90443 7.6941 3.41459 8.26258 3.1123C8.83107 2.81001 9.4867 2.71394 10.118 2.84043C10.7493 2.96691 11.3173 3.30814 11.7255 3.80609C12.1337 4.30405 12.3568 4.92799 12.3569 5.57185C12.3569 9.21471 6.99972 12.2147 6.99972 12.2147Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {image.meta?.likes ?? '-'}
              </DownloadCount>
            )}
            {!image.meta!.licensed && (
              <LightBoxButton onClick={onClick}>
                <svg
                  width="17"
                  height="18"
                  viewBox="0 0 17 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.57888 14.5383C11.1335 14.5383 14.0152 11.6454 14.0152 8.07677C14.0152 4.50816 11.1335 1.61523 7.57888 1.61523C4.02421 1.61523 1.14258 4.50816 1.14258 8.07677C1.14258 11.6454 4.02421 14.5383 7.57888 14.5383Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12.1309 12.646L15.8548 16.3845"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </LightBoxButton>
            )}
            <DownloadButton
              as="a"
              download
              $standalone={!!image.meta!.licensed}
              href={image.localUrl}
              onClick={onDownload}
            >
              {type === 'getty' ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.64556 4.4647L8.883 3.22726C9.13832 2.9717 9.44149 2.76896 9.77518 2.6306C10.1089 2.49224 10.4666 2.42098 10.8278 2.4209C11.1891 2.42081 11.5468 2.4919 11.8805 2.63011C12.2143 2.76831 12.5176 2.97092 12.773 3.22635C13.0284 3.48179 13.231 3.78505 13.3692 4.11881C13.5074 4.45258 13.5785 4.8103 13.5785 5.17154C13.5784 5.53278 13.5071 5.89047 13.3688 6.22416C13.2304 6.55786 13.0276 6.86103 12.7721 7.11635L11.0043 8.88412C10.749 9.13948 10.4458 9.34204 10.1122 9.48024C9.77851 9.61844 9.42091 9.68957 9.05978 9.68957C8.69864 9.68957 8.34104 9.61844 8.0074 9.48024C7.67375 9.34204 7.3706 9.13948 7.11523 8.88412"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.35281 11.5354L7.11537 12.7729C6.86005 13.0284 6.55688 13.2312 6.22319 13.3695C5.88949 13.5079 5.5318 13.5791 5.17056 13.5792C4.80932 13.5793 4.4516 13.5082 4.11784 13.37C3.78408 13.2318 3.48082 13.0292 3.22538 12.7738C2.96994 12.5183 2.76733 12.2151 2.62913 11.8813C2.49093 11.5475 2.41984 11.1898 2.41992 10.8286C2.42001 10.4673 2.49126 10.1096 2.62962 9.77595C2.76798 9.44226 2.97073 9.13909 3.22628 8.88377L4.99405 7.116C5.24941 6.86064 5.55257 6.65808 5.88621 6.51988C6.21986 6.38168 6.57746 6.31055 6.93859 6.31055C7.29973 6.31055 7.65733 6.38168 7.99097 6.51988C8.32462 6.65808 8.62778 6.86064 8.88314 7.116"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.6354 10.0008C16.6354 13.665 13.665 16.6354 10.0008 16.6354C6.33663 16.6354 3.36621 13.665 3.36621 10.0008C3.36621 6.33663 6.33663 3.36621 10.0008 3.36621C13.665 3.36621 16.6354 6.33663 16.6354 10.0008Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                  />
                  <path
                    d="M7.38965 10.4668L10.0005 13.0776L12.6113 10.4668"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 6.92383V13.0777"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </DownloadButton>
            {type === 'midjourney' && (
              <FavoritesButton
                $isFavorite={image.meta?.isFavorite}
                onClick={onToggleFavorite}
              >
                {image.meta?.isFavorite ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="15"
                    viewBox="0 0 16 15"
                    fill="none"
                  >
                    <path
                      d="M8.33931 12.5049L12.219 14.9015C12.7149 15.2078 13.3306 14.7522 13.1834 14.1878L12.0625 9.88862C12.031 9.76888 12.0347 9.64288 12.0733 9.52512C12.1119 9.40736 12.1838 9.30261 12.2808 9.22293L15.7598 6.39967C16.2169 6.02871 15.981 5.28901 15.3937 5.25185L10.8503 4.96435C10.7279 4.95583 10.6106 4.91359 10.5119 4.84254C10.4132 4.7715 10.3372 4.67457 10.2928 4.56306L8.5983 0.402583C8.55224 0.284222 8.47034 0.182322 8.36347 0.110416C8.25661 0.03851 8.12984 0 8 0C7.87016 0 7.74339 0.03851 7.63653 0.110416C7.52967 0.182322 7.44777 0.284222 7.40171 0.402583L5.70722 4.56306C5.66282 4.67457 5.58685 4.7715 5.48814 4.84254C5.38944 4.91359 5.27206 4.95583 5.14969 4.96435L0.60631 5.25185C0.0189969 5.28901 -0.216946 6.02871 0.240183 6.39967L3.71921 9.22293C3.81616 9.30261 3.88808 9.40736 3.92669 9.52512C3.96531 9.64288 3.96904 9.76888 3.93748 9.88862L2.89796 13.8756C2.72137 14.5529 3.46023 15.0997 4.05533 14.7321L7.66069 12.5049C7.76209 12.442 7.8798 12.4086 8 12.4086C8.1202 12.4086 8.23791 12.442 8.33931 12.5049Z"
                      fill="#ED911B"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="15"
                    viewBox="0 0 16 15"
                    fill="none"
                  >
                    <path
                      d="M8.33931 12.5049L12.219 14.9015C12.7149 15.2078 13.3306 14.7522 13.1834 14.1878L12.0625 9.88862C12.031 9.76888 12.0347 9.64288 12.0733 9.52512C12.1119 9.40736 12.1838 9.30261 12.2808 9.22293L15.7598 6.39967C16.2169 6.02871 15.981 5.28901 15.3937 5.25185L10.8503 4.96435C10.7279 4.95583 10.6106 4.91359 10.5119 4.84254C10.4132 4.7715 10.3372 4.67457 10.2928 4.56306L8.5983 0.402583C8.55224 0.284222 8.47034 0.182322 8.36347 0.110416C8.25661 0.03851 8.12984 0 8 0C7.87016 0 7.74339 0.03851 7.63653 0.110416C7.52967 0.182322 7.44777 0.284222 7.40171 0.402583L5.70722 4.56306C5.66282 4.67457 5.58685 4.7715 5.48814 4.84254C5.38944 4.91359 5.27206 4.95583 5.14969 4.96435L0.60631 5.25185C0.0189969 5.28901 -0.216946 6.02871 0.240183 6.39967L3.71921 9.22293C3.81616 9.30261 3.88808 9.40736 3.92669 9.52512C3.96531 9.64288 3.96904 9.76888 3.93748 9.88862L2.89796 13.8756C2.72137 14.5529 3.46023 15.0997 4.05533 14.7321L7.66069 12.5049C7.76209 12.442 7.8798 12.4086 8 12.4086C8.1202 12.4086 8.23791 12.442 8.33931 12.5049Z"
                      fill="#E9E9E9"
                    />
                  </svg>
                )}
              </FavoritesButton>
            )}
            {!image.meta!.licensed && (
              <StyledImageToolCheckbox
                index={image.id}
                isChecked={selectedImages.includes(image.id)}
                setSelectedImages={setSelectedImages}
              />
            )}
            {type === 'midjourney' && !!image.meta?.query && (
              <MoreButtonHolder>
                <MoreButton onClick={() => setShowMjDropdown((prev) => !prev)}>
                  {showMJDropdown ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <rect
                        width="12"
                        height="12"
                        transform="translate(0.515625 9) rotate(-45)"
                        fill="#F8F8F8"
                      />
                      <path
                        d="M5.36523 5.36328L12.6383 12.6364"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.36523 12.6367L12.6383 5.36362"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <rect width="12" height="12" fill="#F8F8F8" />
                      <path
                        d="M6 0.857422V11.1431"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M0.857422 6H11.1431"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </MoreButton>
                {showMJDropdown && (
                  <Dropdown>
                    <StyledActionsDropdownButton
                      as="button"
                      disabled={extraMJdisabled}
                      onClick={(e: MouseEvent<HTMLButtonElement>) =>
                        extraMJGenerate(e, 'query')
                      }
                    >
                      <span>
                        <RingsIcon />
                      </span>
                      Get more results with image querie
                    </StyledActionsDropdownButton>
                    <StyledActionsDropdownButton
                      as="button"
                      disabled={extraMJdisabled}
                      onClick={(e: MouseEvent<HTMLButtonElement>) =>
                        extraMJGenerate(e, 'visual')
                      }
                    >
                      <span>
                        <PencilIcon />
                      </span>
                      Get more results using image visual
                    </StyledActionsDropdownButton>
                    <StyledActionsDropdownButton
                      as="button"
                      disabled={extraMJdisabled}
                      onClick={(e: MouseEvent<HTMLButtonElement>) =>
                        extraMJGenerate(e, 'prompt')
                      }
                    >
                      <span>
                        <CodeIcon />
                      </span>
                      Add additional prompt for this image
                    </StyledActionsDropdownButton>
                  </Dropdown>
                )}
              </MoreButtonHolder>
            )}
          </>
        ) : (
          <PlayButton
            href={image.meta!.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="14"
              viewBox="0 0 12 14"
              fill="none"
            >
              <path d="M12 7L0 14L0 0L12 7Z" fill="white" />
            </svg>
          </PlayButton>
        )}
      </ImageHolder>
      {type === 'midjourney' && image.meta?.query && (
        <PromptBox text={image.meta.query} />
      )}
      {type === 'instagram' && (
        <InstaBox>
          <InstaText $type="smallBold">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.99916 10.8567C10.5239 10.8567 12.5706 8.81003 12.5706 6.2853C12.5706 3.76057 10.5239 1.71387 7.99916 1.71387C5.47443 1.71387 3.42773 3.76057 3.42773 6.2853C3.42773 8.81003 5.47443 10.8567 7.99916 10.8567Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
              />
              <path
                d="M1.07031 14.8568C1.77286 13.6407 2.78304 12.631 3.99937 11.929C5.21571 11.227 6.59537 10.8574 7.99975 10.8574C9.40413 10.8574 10.7838 11.227 12.0001 11.9291C13.2164 12.6311 14.2266 13.6409 14.9291 14.8569"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {image.meta?.userName ?? '-'}
          </InstaText>
          <InstaText $type="smallBold">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.71484 5.71387H14.2863"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1.71484 10.2861H14.2863"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.2852 1.71387V14.2853"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.71484 1.71387V14.2853"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {image.meta?.hashTag ?? '-'}
          </InstaText>
          <InstaLink $type="smallBold">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.57617 10.4239L10.4249 5.5752"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.21267 11.6368L7.19237 13.6571C6.54935 14.2999 5.67733 14.661 4.76812 14.6609C3.85891 14.6608 2.98696 14.2996 2.34405 13.6567C1.70114 13.0138 1.33992 12.1418 1.33984 11.2326C1.33977 10.3234 1.70083 9.45141 2.34363 8.80839L4.36394 6.78809"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.6358 9.21169L13.6561 7.19139C14.2989 6.54837 14.66 5.67636 14.6599 4.76715C14.6599 3.85793 14.2986 2.98598 13.6557 2.34307C13.0128 1.70016 12.1409 1.33894 11.2317 1.33887C10.3224 1.33879 9.45044 1.69986 8.80742 2.34266L6.78711 4.36296"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <a href={image.meta?.link ?? '#'}>{image.meta?.link ?? ''}</a>
          </InstaLink>
        </InstaBox>
      )}
      <AnimatePresence>
        {showPromptModal && (
          <PromptModal
            onClose={() => setShowPromptModal(false)}
            mediaId={image.id}
          />
        )}
      </AnimatePresence>
    </Box>
  );
};

export default ImageToolImage;
